import { EditRowColumnName } from 'enums/EditRowColumnName';
import { EditRowDataKey } from 'enums/EditRowDataKey';
import {
  ICellRendererParams,
  valueFormatters,
  Dexterity,
  CallTypes,
  IBulkEditorOptions,
  IDisplayInning,
  IBulkEditorParams,
  svAppBulkPlayerEditorRenderer,
  pitchCallRenderer,
  CallTypesExtended,
  bulkHotkeyEditorRenderer,
  enumToDropdownWithHotkeys,
  BallsEnum,
  newBulkInningEditorRenderer,
  IInning,
  InningNumbers,
  InningSides,
  RightLeftHotkey,
  KOrBBHotkey,
  ResultTypesHotkey,
  svAppInningRenderer,
  svAppSimpleHeaderRenderer,
  svAppPlayerRenderer,
  svAppSimpleTextRenderer,
  svAppMeasurementRenderer,
  twoColumnBulkHotkeyEditorRenderer,
  svAppTimestampRenderer,
} from '@trackman/baseball-shared-components';
import { IDataGridColumnConfiguration } from '@trackman/baseball-shared-components/build/models/NewDataGrid/IDataGridColumnConfiguration';
import { PlayRow } from 'models/PlayEditRow/PlayRow';
import { TabsNames } from 'enums/TabsNames';
import baseRunnerRenderer from './baseRunnerRenderer';
import defensivePlayerRenderer from './defensivePlayerRenderer';
import { PitchTypesHotkey } from './PitchOptions';
import { HitTypesHotkey } from './HitOptions';

const trackStartTimeLocalColumn = {
  dataKey: EditRowDataKey.TrackStartTimeLocal,
  columnName: EditRowColumnName.TrackStartTimeLocal,
  cellRenderer: svAppTimestampRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 105,
  isChecked: true,
  type: [TabsNames.BallTracking, TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const trackStartTimeUtcColumn = {
  dataKey: EditRowDataKey.TrackStartTimeUtc,
  columnName: EditRowColumnName.TrackStartTimeUtc,
  cellRenderer: svAppTimestampRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 105,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const pitcherColumn = {
  dataKey: EditRowDataKey.Pitcher,
  columnName: EditRowColumnName.Pitcher,
  cellRenderer: svAppPlayerRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 150,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const catcherColumn = {
  dataKey: EditRowDataKey.Catcher,
  columnName: EditRowColumnName.Catcher,
  cellRenderer: svAppPlayerRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 150,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const batterColumn = {
  dataKey: EditRowDataKey.Batter,
  columnName: EditRowColumnName.Batter,
  cellRenderer: svAppPlayerRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 150,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const ballsColumn = {
  dataKey: EditRowDataKey.Balls,
  columnName: EditRowColumnName.Balls,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) => bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(BallsEnum)),
    defaultValue: 0,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const strikesColumn = {
  dataKey: EditRowDataKey.Strikes,
  columnName: EditRowColumnName.Strikes,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) => bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(BallsEnum)),
    defaultValue: 0,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const outsColumn = {
  dataKey: EditRowDataKey.Outs,
  columnName: EditRowColumnName.Outs,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) => bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(BallsEnum)),
    defaultValue: 0,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const inningColumn = {
  dataKey: EditRowDataKey.Inning,
  columnName: EditRowColumnName.Inning,
  cellRenderer: svAppInningRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  bulkEditorOptions: {
    renderer: (params: IBulkEditorParams<IInning, PlayRow, void>) =>
      newBulkInningEditorRenderer(params, enumToDropdownWithHotkeys(InningNumbers), enumToDropdownWithHotkeys(InningSides)),
    defaultValue: { inningNumber: 1, isTopInning: true } as IDisplayInning,
  } as IBulkEditorOptions<IDisplayInning>,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const pitcherDexterityColumn = {
  dataKey: EditRowDataKey.PitcherDexterity,
  columnName: EditRowColumnName.PitcherDexterity,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 110,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) =>
      bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(RightLeftHotkey)),
    defaultValue: Dexterity.Undefined,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const battingSideColumn = {
  dataKey: EditRowDataKey.BatterDexterity,
  columnName: EditRowColumnName.BattingSide,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 110,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) =>
      bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(RightLeftHotkey)),
    defaultValue: Dexterity.Undefined,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const taggedPitchColumn = {
  dataKey: EditRowDataKey.TaggedPitchType,
  columnName: EditRowColumnName.TaggedPitch,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 175,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) =>
      twoColumnBulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(PitchTypesHotkey)),
    defaultValue: PitchTypesHotkey.Undefined.value,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const pitchCallColumn = {
  dataKey: EditRowDataKey.PitchCall,
  columnName: EditRowColumnName.PitchCall,
  cellRenderer: (data: ICellRendererParams<string, PlayRow>) => pitchCallRenderer(data),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 150,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) =>
      twoColumnBulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(CallTypesExtended)),
    defaultValue: CallTypes.Undefined,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const kOrBBColumn = {
  dataKey: EditRowDataKey.KOrBB,
  columnName: EditRowColumnName.KOrBB,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 120,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) => bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(KOrBBHotkey)),
    defaultValue: KOrBBHotkey.Undefined.value,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const hitTypeColumn = {
  dataKey: EditRowDataKey.HitType,
  columnName: EditRowColumnName.HitType,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 140,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) => bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(HitTypesHotkey)),
    defaultValue: HitTypesHotkey.Undefined.value,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const playResultColumn = {
  dataKey: EditRowDataKey.PlayResult,
  columnName: EditRowColumnName.PlayResult,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 140,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, PlayRow, void>) =>
      bulkHotkeyEditorRenderer(data, enumToDropdownWithHotkeys(ResultTypesHotkey)),
    defaultValue: ResultTypesHotkey.Undefined,
  },
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const outsOnPlayColumn = {
  dataKey: EditRowDataKey.OutsOnPlay,
  columnName: EditRowColumnName.OutsOnPlay,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  bulkEditorOptions: {
    renderer: (params: IBulkEditorParams<number, PlayRow, void>) => bulkHotkeyEditorRenderer(params, enumToDropdownWithHotkeys(BallsEnum)),
    defaultValue: 0,
  } as IBulkEditorOptions<number>,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const runsScoredColumn = {
  dataKey: EditRowDataKey.RunsScored,
  columnName: EditRowColumnName.RunsScored,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  bulkEditorOptions: {
    renderer: (params: IBulkEditorParams<number, PlayRow, void>) => bulkHotkeyEditorRenderer(params, enumToDropdownWithHotkeys(BallsEnum)),
    defaultValue: 0,
  } as IBulkEditorOptions<number>,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const pAofInningColumn = {
  dataKey: EditRowDataKey.PAofInning,
  columnName: EditRowColumnName.PAofInning,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const pitchOfPAColumn = {
  dataKey: EditRowDataKey.PitchOfPA,
  columnName: EditRowColumnName.PitchOfPA,
  cellRenderer: svAppSimpleTextRenderer,
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const releaseSpeedColumn = {
  dataKey: EditRowDataKey.ReleaseSpeed,
  columnName: EditRowColumnName.ReleaseSpeed,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.mphFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const verticalReleaseAngleColumn = {
  dataKey: EditRowDataKey.VerticalReleaseAngle,
  columnName: EditRowColumnName.VerticalReleaseAngle,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) =>
    svAppMeasurementRenderer(data, valueFormatters.onePlaceDecimalValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 85,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const horizontalReleaseAngleColumn = {
  dataKey: EditRowDataKey.HorizontalReleaseAngle,
  columnName: EditRowColumnName.HorizontalReleaseAngle,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) =>
    svAppMeasurementRenderer(data, valueFormatters.onePlaceDecimalValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 85,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const spinRateColumn = {
  dataKey: EditRowDataKey.SpinRate,
  columnName: EditRowColumnName.SpinRate,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const spinAxisColumn = {
  dataKey: EditRowDataKey.SpinAxis,
  columnName: EditRowColumnName.SpinAxis,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const tiltColumn = {
  dataKey: EditRowDataKey.Tilt,
  columnName: EditRowColumnName.Tilt,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.angleClockFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const releaseHeightColumn = {
  dataKey: EditRowDataKey.ReleaseHeight,
  columnName: EditRowColumnName.ReleaseHeight,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const releaseSideColumn = {
  dataKey: EditRowDataKey.ReleaseSide,
  columnName: EditRowColumnName.ReleaseSide,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const extensionColumn = {
  dataKey: EditRowDataKey.Extension,
  columnName: EditRowColumnName.Extension,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const verticalBreakColumn = {
  dataKey: EditRowDataKey.VerticalBreak,
  columnName: EditRowColumnName.VerticalBreak,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.inchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const inducedVerticalBreakColumn = {
  dataKey: EditRowDataKey.InducedVerticalBreak,
  columnName: EditRowColumnName.InducedVerticalBreak,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.inchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const horizontalBreakColumn = {
  dataKey: EditRowDataKey.HorizontalBreak,
  columnName: EditRowColumnName.HorizontalBreak,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.inchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const plateLocHeightColumn = {
  dataKey: EditRowDataKey.PlateLocHeight,
  columnName: EditRowColumnName.PlateLocHeight,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const plateLocSideColumn = {
  dataKey: EditRowDataKey.PlateLocSide,
  columnName: EditRowColumnName.PlateLocSide,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 90,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const hitExitSpeedColumn = {
  dataKey: EditRowDataKey.HitExitSpeed,
  columnName: EditRowColumnName.HitExitSpeed,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.mphFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const hitEstCarryColumn = {
  dataKey: EditRowDataKey.HitEstCarry,
  columnName: EditRowColumnName.HitEstCarry,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const hitDirectionColumn = {
  dataKey: EditRowDataKey.HitDirection,
  columnName: EditRowColumnName.HitDirection,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const hitBearingColumn = {
  dataKey: EditRowDataKey.HitBearing,
  columnName: EditRowColumnName.HitBearing,
  cellRenderer: (data: ICellRendererParams<number, PlayRow>) => svAppMeasurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 75,
  isChecked: true,
  type: [TabsNames.BallTracking],
  readonly: true,
} as IDataGridColumnConfiguration;

const firstBaseColumn = {
  dataKey: EditRowDataKey.FirstBase,
  columnName: EditRowColumnName.RunnerOnFirst,
  // TODO: move renderers to BSC
  cellRenderer: baseRunnerRenderer,
  bulkEditorOptions: {
    renderer: (params: any) => svAppBulkPlayerEditorRenderer(params, true),
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const secondBaseColumn = {
  dataKey: EditRowDataKey.SecondBase,
  columnName: EditRowColumnName.RunnerOnSecond,
  // TODO: move renderers to BSC
  cellRenderer: baseRunnerRenderer,
  bulkEditorOptions: {
    renderer: (params: any) => svAppBulkPlayerEditorRenderer(params, true),
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const thirdBaseColumn = {
  dataKey: EditRowDataKey.ThirdBase,
  columnName: EditRowColumnName.RunnerOnThird,
  // TODO: move renderers to BSC
  cellRenderer: baseRunnerRenderer,
  bulkEditorOptions: {
    renderer: (params: any) => svAppBulkPlayerEditorRenderer(params, true),
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const firstBaseman = {
  dataKey: EditRowDataKey.FirstBaseman,
  columnName: EditRowColumnName.FirstBaseman,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const secondBaseman = {
  dataKey: EditRowDataKey.SecondBaseman,
  columnName: EditRowColumnName.SecondBaseman,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const thirdBaseman = {
  dataKey: EditRowDataKey.ThirdBaseman,
  columnName: EditRowColumnName.ThirdBaseman,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const shortStopper = {
  dataKey: EditRowDataKey.ShortStopper,
  columnName: EditRowColumnName.ShortStopper,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const leftFielder = {
  dataKey: EditRowDataKey.LeftFielder,
  columnName: EditRowColumnName.LeftFielder,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const centerFielder = {
  dataKey: EditRowDataKey.CenterFielder,
  columnName: EditRowColumnName.CenterFielder,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

const rightFielder = {
  dataKey: EditRowDataKey.RightFielder,
  columnName: EditRowColumnName.RightFielder,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: svAppBulkPlayerEditorRenderer,
  },
  headerRenderer: svAppSimpleHeaderRenderer,
  width: 200,
  isChecked: true,
  type: [TabsNames.PlayerTracking],
  readonly: false,
} as IDataGridColumnConfiguration;

export const BallTrackingColumns = [
  trackStartTimeLocalColumn,
  trackStartTimeUtcColumn,
  releaseHeightColumn,
  releaseSideColumn,
  extensionColumn,
  plateLocHeightColumn,
  plateLocSideColumn,
  pitcherColumn,
  pitcherDexterityColumn,
  catcherColumn,
  batterColumn,
  battingSideColumn,
  ballsColumn,
  strikesColumn,
  outsColumn,
  inningColumn,
  taggedPitchColumn,
  pitchCallColumn,
  kOrBBColumn,
  hitTypeColumn,
  playResultColumn,
  outsOnPlayColumn,
  runsScoredColumn,
  pAofInningColumn,
  pitchOfPAColumn,
  releaseSpeedColumn,
  verticalReleaseAngleColumn,
  horizontalReleaseAngleColumn,
  spinRateColumn,
  spinAxisColumn,
  tiltColumn,
  verticalBreakColumn,
  inducedVerticalBreakColumn,
  horizontalBreakColumn,
  hitExitSpeedColumn,
  hitEstCarryColumn,
  hitDirectionColumn,
  hitBearingColumn,
];
export const PlayerTrackingColumns = [
  trackStartTimeLocalColumn, // localTime cannot be hidden column
  firstBaseColumn,
  secondBaseColumn,
  thirdBaseColumn,
  firstBaseman,
  secondBaseman,
  thirdBaseman,
  shortStopper,
  leftFielder,
  centerFielder,
  rightFielder,
];

export const AllColumns = [
  // updatesColumn,
  ...BallTrackingColumns,
  ...PlayerTrackingColumns,
];

export const getColumn = (dataKey: string) => {
  return AllColumns.find((x) => x.dataKey === dataKey);
};
